import { decodeFunctionData, encodeFunctionData, type Address, type Hex } from "viem";
import { ponsRewardNftABI } from "../../../abis/pons-reward-nft";
import { xMintAuthorizationSchema, type XMintAuthorization } from "../api/social-tasks-schemas";
import { ELYSIUM, isElysiumFreePolicy } from "../../../config/elysium";

/** Validate the supplied bytes; only the original backend calldata may be sent. */
export function decodeTaskMint(result: XMintAuthorization, expected: {
  wallet: string; sourceToken: string; chainId: number; collection: string;
}) {
  xMintAuthorizationSchema.parse(result);
  if (result.chainId !== expected.chainId || !same(result.contractAddress, expected.collection)) {
    throw new Error("Task mint deployment mismatch");
  }
  const decoded = decodeTaskCall(result.call.data as Hex);
  if (decoded.functionName !== "mint") throw new Error("Task authorization is not a mint call");
  const [authorization, signature] = decoded.args;
  if (expected.chainId === ELYSIUM.chainId && !isElysiumFreePolicy(authorization.rewardPolicyVersion, authorization.rewardPolicyHash))
    throw new Error("Social task reward policy mismatch");
  if (!same(authorization.wallet, expected.wallet) || !same(authorization.sourceToken, expected.sourceToken) ||
    authorization.selectedTierUsd !== 0n || authorization.quotedMintFeeWei !== 0n ||
    authorization.deadline !== BigInt(result.deadline) || authorization.issuedAt >= authorization.deadline ||
    !/^0x[0-9a-f]{130}$/i.test(signature) ||
    encodeFunctionData({ abi: ponsRewardNftABI, functionName: "mint", args: decoded.args }).toLowerCase() !== result.call.data.toLowerCase()) {
    throw new Error("Task mint calldata differs from the reviewed wallet, source, zero tier/value or deadline");
  }
  return { authorization, signature, identity: {
    route: "SOCIAL_TASK" as const, proofKey: authorization.proofKey, authorizationId: authorization.authorizationId,
    tierUsd: "0", feeWei: "0", policyHash: authorization.rewardPolicyHash, policyVersion: authorization.rewardPolicyVersion.toString(),
  } };
}

export type TaskMintIdentity = ReturnType<typeof decodeTaskMint>["identity"];
export type TaskMintBinding = { wallet: Address; sourceToken: Address; chainId: number; collection: Address };
function decodeTaskCall(data: Hex) {
  try { return decodeFunctionData({ abi: ponsRewardNftABI, data }); }
  catch { throw new Error("Invalid task mint calldata"); }
}
function same(a: string, b: string) { return a.toLowerCase() === b.toLowerCase(); }
